// src/stage2/sceneExterior/Ring.jsx
import React, { useRef, useState, useEffect, useCallback } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

const SOUND_PATHS = [
  "/stageAssets/stage2/sounds/accomplishment1.mp3",
  "/stageAssets/stage2/sounds/accomplishment2.mp3",
  "/stageAssets/stage2/sounds/accomplishment3.mp3",
  "/stageAssets/stage2/sounds/accomplishment4.mp3",
  "/stageAssets/stage2/sounds/accomplishment6.mp3",
  "/stageAssets/stage2/sounds/accomplishment7.mp3",
  "/stageAssets/stage2/sounds/accomplishment8.mp3",
];

const tmpVec = new THREE.Vector3();

export default function Ring({ ringId, position, scale, color, onCollect }) {
  const groupRef = useRef();
  const { camera } = useThree();

  const soundsRef = useRef([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [collected, setCollected] = useState(false);

  useEffect(() => {
    soundsRef.current = SOUND_PATHS.map((src) => {
      const audio = new Audio(src);
      audio.preload = "auto";
      return audio;
    });
  }, []);

  const handleSoundPlay = useCallback(() => {
    const sounds = soundsRef.current;
    if (!sounds.length || isPlaying) return;

    const randomIndex = Math.floor(Math.random() * sounds.length);
    const selectedSound = sounds[randomIndex];

    selectedSound.currentTime = 0;
    setIsPlaying(true);

    selectedSound.onended = () => setIsPlaying(false);

    selectedSound.play().catch(() => {
      setIsPlaying(false);
    });
  }, [isPlaying]);

  const collect = useCallback(() => {
    if (collected) return;

    setCollected(true);
    handleSoundPlay();

    if (typeof onCollect === "function") {
      onCollect(ringId);
    }
  }, [collected, handleSoundPlay, onCollect, ringId]);

  useFrame((state, delta) => {
    if (!groupRef.current || collected) return;

    // spin
    groupRef.current.rotation.y += 0.5 * delta;
    groupRef.current.rotation.x += 0.5 * delta;

    // distance-based collect trigger
    const worldPos = groupRef.current.getWorldPosition(tmpVec);
    const dist = camera.position.distanceTo(worldPos);

    if (dist < 1.2) {
      collect();
    }
  });

  // hide once collected
  if (collected) return null;

  return (
    <group
      ref={groupRef}
      position={position}
      scale={scale}
      onClick={(e) => {
        e.stopPropagation();
        collect();
      }}
      onPointerOver={() => (document.body.style.cursor = "pointer")}
      onPointerOut={() => (document.body.style.cursor = "default")}
    >
      <mesh>
        <torusGeometry args={[1, 0.3, 12, 50]} />
        <meshStandardMaterial color={color} />
      </mesh>
    </group>
  );
}
